import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";

import { AuthLayout } from "@/components/auth/AuthLayout";
import {
  DefinirPasswordForm,
  passwordSchema,
  type DefinirPasswordValues,
} from "@/components/auth/DefinirPasswordForm";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAceptarInvitacion, useValidarInvitacion } from "@/hooks/useInvitacion";
import { ApiError, getApiErrorMessage, parseValidationErrors } from "@/lib/api";

/**
 * Pantalla a la que llega el invitado desde el enlace del correo. Valida el
 * token contra la API y, si es válido, deja definir la contraseña inicial
 * (no hay contraseña en el alta — ver NuevoUsuarioDialog).
 */
export function ActivarCuenta() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const validacion = useValidarInvitacion(token, email);
  const aceptar = useAceptarInvitacion();

  const form = useForm<DefinirPasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: "", password_confirmation: "" },
  });

  function handleSubmit(values: DefinirPasswordValues) {
    if (!token || !email) return;

    aceptar.mutate(
      { token, email, ...values },
      {
        onSuccess: () => {
          toast.success("Contraseña creada. Ya puedes iniciar sesión.");
          navigate("/login");
        },
        onError: (error) => {
          const errores = parseValidationErrors(error);
          const camposConError = errores
            ? (["password", "password_confirmation"] as const).filter((campo) => errores[campo])
            : [];

          if (camposConError.length > 0) {
            for (const campo of camposConError) {
              form.setError(campo, { type: "server", message: errores![campo] });
            }
            return;
          }

          toast.error(
            getApiErrorMessage(error, "No pudimos activar tu cuenta. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  // Enlace incompleto (falta token o email en la URL): ni vale la pena pegarle a la API.
  if (!token || !email) {
    return (
      <AuthLayout>
        <EstadoInvalido
          titulo="Enlace inválido"
          descripcion="Este enlace de invitación no es válido. Pide que te reenvíen la invitación."
        />
      </AuthLayout>
    );
  }

  if (validacion.isLoading) {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Spinner className="text-primary size-6" />
          <p className="text-muted-foreground text-caption">Validando tu invitación…</p>
        </div>
      </AuthLayout>
    );
  }

  if (validacion.isError) {
    const status = validacion.error instanceof ApiError ? validacion.error.status : null;

    if (status === 410) {
      return (
        <AuthLayout>
          <EstadoInvalido
            icon={<Clock className="h-6 w-6" />}
            titulo="Enlace expirado"
            descripcion="Esta invitación venció o ya fue utilizada. Pide a un administrador que te reenvíe una nueva."
          />
        </AuthLayout>
      );
    }

    return (
      <AuthLayout>
        <EstadoInvalido
          titulo="Enlace inválido"
          descripcion="No pudimos validar esta invitación. Verifica el enlace o pide que te reenvíen uno nuevo."
        />
      </AuthLayout>
    );
  }

  if (!validacion.data) {
    // Estado transitorio (p. ej. justo antes de que `isLoading` refleje el
    // fetch en curso): evitamos reventar contra `undefined` y mostramos el
    // mismo loader de validación.
    return (
      <AuthLayout>
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <Spinner className="text-primary size-6" />
          <p className="text-muted-foreground text-caption">Validando tu invitación…</p>
        </div>
      </AuthLayout>
    );
  }

  const invitacion = validacion.data;

  return (
    <AuthLayout>
      <div className="mb-8">
        <div className="bg-accent text-accent-foreground mb-5 flex h-12 w-12 items-center justify-center rounded-2xl">
          <CheckCircle2 className="h-6 w-6" />
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">
          ¡Hola, {invitacion.first_name}!
        </h1>
        <p className="text-muted-foreground text-caption mt-2">
          Crea una contraseña para activar tu cuenta de Consultek.
        </p>
      </div>

      <DefinirPasswordForm
        form={form}
        onSubmit={handleSubmit}
        isSubmitting={aceptar.isPending}
        submitLabel="Activar cuenta"
      />
    </AuthLayout>
  );
}

function EstadoInvalido({
  icon = <AlertTriangle className="h-6 w-6" />,
  titulo,
  descripcion,
}: {
  icon?: React.ReactNode;
  titulo: string;
  descripcion: string;
}) {
  return (
    <div className="mb-8">
      <div className="bg-destructive/10 text-destructive mb-5 flex h-12 w-12 items-center justify-center rounded-2xl">
        {icon}
      </div>
      <h1 className="text-3xl font-extrabold tracking-tight">{titulo}</h1>
      <p className="text-muted-foreground text-caption mt-2">{descripcion}</p>
      <Button variant="outline" size="lg" className="mt-8 w-full" asChild>
        <Link to="/login">Volver a iniciar sesión</Link>
      </Button>
    </div>
  );
}
